import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { TenantSettings as TenantSettingsList } from '@s20/common/constants/tenant';
import { KeysToCamelCase } from '@s20/common/types';
import i18n, { SUPPORTED_LANGUAGES, type SupportedLanguage } from '@s20/i18n';

enum Currencies {
    USD = 'USD',
    CAD = 'CAD',
}
interface TenantTheme {
    readonly tenantCompanyId: number;
    readonly defaultColor?: string | null;
    readonly primaryColor: string;
    readonly secondaryColor: string;
    readonly successColor: string;
    readonly warningColor: string;
    readonly dangerColor: string;
    readonly infoColor: string;
    readonly defaultHighlightColor: string;
    readonly defaultHighlightAccentColor: string;
    readonly showBrandLabel: boolean;
}

type TenantSettingsListType = KeysToCamelCase<typeof TenantSettingsList>;

export type TenantSettingsTypes = string | number | boolean | TenantTheme;
export interface TenantSettingsListTypeLowercase extends TenantSettingsListType, Record<string, TenantSettingsTypes> {}

interface TenantSettings extends Record<string, TenantSettingsTypes>, TenantSettingsListTypeLowercase {
    [key: string]: TenantSettingsTypes;
    readonly tenantTheme: TenantTheme;
}

interface Tenant {
    readonly id: number;
    readonly companyName: string;
    readonly countryId: number;
    readonly countryCode: string;
    readonly defaultCurrencyCode: Currencies;
}

interface Group {
    readonly id: number;
    readonly companyId: number;
    readonly groupName: string;
    readonly description: string;
    readonly code?: string;
    readonly expandDispatching: boolean;
    readonly expanededDispatchingCompanies: number[];
}

interface Role {
    readonly id: string;
    readonly name: string;
    readonly displayName: string;
}

interface User {
    readonly userId: string;
    readonly userName: string;
    readonly firstName: string;
    readonly lastName: string;
    readonly email: string;
    readonly fullName: string;
    readonly roles: Role[];
    readonly groups: Group[];
    readonly appGroups: number[];
    readonly tenantId: number;
    readonly companyId: number;
    readonly companyTenantId: number;
    readonly dispatchCompanyId: number;
    readonly employeeId: number;
    readonly isImpersonating?: boolean;
    readonly isAdmin: boolean;
    readonly shortcuts: Shortcut[];
    readonly hasBetaAccess: boolean;
    readonly isFlaggerAvailableForOnCall: boolean;
    readonly isOnCallRestricted: boolean;
    readonly excludeDispatch: boolean;
    readonly isFieldGroupMember: boolean;
    readonly preferredLanguage: SupportedLanguage;
}

interface Shortcut {
    readonly id: string;
    readonly order: number;
    readonly name: string;
    readonly active: boolean;
}

interface Company {
    readonly id: number;
    readonly companyName: string;
}

export interface CommonState {
    readonly isAuthenticated?: boolean | null;
    readonly pageTitle: string;
    readonly formIsOpen: boolean;
    readonly version: string;
    readonly user?: User | null;
    readonly tenant?: Tenant | null;
    readonly tenantSettings?: TenantSettings | null;
    readonly tenantTheme?: TenantTheme | null;
    readonly timesheetNotifications: number;
    readonly allTenants: Company[];
}

const initialState: CommonState = {
    isAuthenticated: null,
    pageTitle: '',
    formIsOpen: false,
    version: '',
    user: null,
    tenant: null,
    tenantSettings: null,
    tenantTheme: null,
    timesheetNotifications: 0,
    allTenants: [],
};

export const commonSlice = createSlice({
    name: 'common',
    initialState,
    reducers: {
        setUserShortcuts: (state, action: PayloadAction<Shortcut[]>) => {
            if (state.user) {
                state.user.shortcuts = action.payload;
            }
        },
        upsertUserShortcut: (state, action: PayloadAction<Shortcut>) => {
            if (state.user?.shortcuts) {
                if (!state.user.shortcuts.find((shortcut) => shortcut.id === action.payload.id)) {
                    state.user.shortcuts.push(action.payload);
                } else {
                    state.user.shortcuts = state.user.shortcuts.map((shortcut) => {
                        if (shortcut.id === action.payload.id) {
                            return action.payload;
                        }

                        return shortcut;
                    });
                }
            }
        },
        setIsAuthenticated: (state, action: PayloadAction<boolean>) => {
            state.isAuthenticated = action.payload;
        },
        setPageTitle: (state, action: PayloadAction<string>) => {
            state.pageTitle = action.payload;
        },
        setFormIsOpen: (state, action: PayloadAction<boolean>) => {
            state.formIsOpen = action.payload;
        },
        setVersion: (state, action: PayloadAction<string>) => {
            state.version = action.payload;
        },
        setUser: (state, action: PayloadAction<User>) => {
            state.user = action.payload;
        },
        setTenant: (state, action: PayloadAction<Tenant>) => {
            state.tenant = action.payload;
        },
        setTenantSettings: (state, action: PayloadAction<TenantSettings>) => {
            state.tenantSettings = action.payload;
        },
        setTenantTheme: (state, action: PayloadAction<TenantTheme>) => {
            state.tenantTheme = action.payload;
        },
        setTimesheetNotifications: (state, action: PayloadAction<number>) => {
            state.timesheetNotifications = action.payload;
        },
        setPreferredLanguage: (state, action: PayloadAction<SupportedLanguage>) => {
            const lang = action.payload;

            if (state.user) {
                state.user.preferredLanguage = lang;
            }

            if (lang && SUPPORTED_LANGUAGES.includes(lang) && i18n.language?.split('-')[0] !== lang) {
                i18n.changeLanguage(lang);
            }
        },
        setAllTenants: (state, action: PayloadAction<Company[]>) => {
            state.allTenants = action.payload;
        },
    },
});

export const {
    setIsAuthenticated, setPageTitle, setFormIsOpen, setVersion,
    setUser, setTenant, setTenantSettings, setTenantTheme,
    setTimesheetNotifications, setUserShortcuts, upsertUserShortcut, setPreferredLanguage,
    setAllTenants,
} = commonSlice.actions;

export default commonSlice.reducer;